import { useState } from 'react';
import { WrenchIcon, PlayIcon } from 'lucide-react';

import { Button, Input, ScrollArea } from '@@/ui/primitives';
import { useQueryByPath } from '../../hooks/use-entities';
import type { QueryByPathResponse } from '../../api/api-entities';
import { PathPreview } from '../path-preview';
import clsx from 'clsx';

export function PathTester() {
  const [path, setPath] = useState('');
  const [result, setResult] = useState<QueryByPathResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const { mutate, isPending } = useQueryByPath();

  const handleTest = (pathToTest: string) => {
    if (!pathToTest.trim()) {
      setError(new Error('Please enter a path to test'));
      return;
    }

    setError(null);

    mutate(
      { path: pathToTest.trim() },
      {
        onSuccess: (data) => {
          setResult(data);
          setError(null);
        },
        onError: (err) => {
          setError(err instanceof Error ? err : new Error('An error occurred'));
          setResult(null);
        }
      }
    );
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTest(path);
    }
  };

  return (
    <div className='flex flex-col bg-white rounded-lg overflow-hidden border border-neutral-200'>
      <div className='flex items-center gap-3 h-12 px-4 text-neutral-950 rounded-t-lg'>
        <WrenchIcon className='size-3.5' />
        <h2 className='text-sm font-semibold'>Path Tester</h2>
      </div>
      <div className=''>
        <div className='flex gap-2 items-center rounded-b-lg px-2 pb-2'>
          <Input
            id='path-input'
            value={path}
            onChange={(e) => setPath(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder='/entity/sub-entity/item-1'
            disabled={isPending}
            className='flex-1 px-0! pl-2! font-mono! tabular-nums text-sm! border-none! rounded-none outline-none shadow-none! focus:ring-0! text-neutral-950!'
          />
          <Button
            variant={'outline'}
            onClick={() => handleTest(path)}
            disabled={isPending}
            size='sm'
            className='text-sm! font-medium! h-7!'
          >
            <PlayIcon className='size-3.5' />
            Run
          </Button>
        </div>

        <ScrollArea
          className={clsx(
            'flex flex-col gap-2! transition-height duration-300 ease-in-out',
            'bg-white',
            'px-4',
            // !result && 'h-0',
            result && 'h-90'
          )}
        >
          <PathPreview
            path={path}
            data={result}
            error={error}
            isPending={isPending}
            onPathClick={(itemPath) => {
              setPath(itemPath);
              handleTest(itemPath);
            }}
          />
        </ScrollArea>
      </div>
    </div>
  );
}
